import { createContext, useContext, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportedLanguages } from '@/i18n/i18next';

interface LanguageContextType {
  language: SupportedLanguages;
  setLanguage: (language: SupportedLanguages) => void;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

const SUPPORTED_LANGUAGES: SupportedLanguages[] = ['en', 'he'];

const resolveLanguage = (lang: string | undefined): SupportedLanguages => {
  if (lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguages)) {
    return lang as SupportedLanguages;
  }
  return 'en';
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<SupportedLanguages>(
    resolveLanguage(i18n.language)
  );

  const setLanguage = (newLanguage: SupportedLanguages) => {
    i18n.changeLanguage(newLanguage);
    setLanguageState(newLanguage);
  };

  const isRTL = language === 'he';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
